import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Code, Zap, Database, Brain, Rocket, Shield, Globe, Sparkles } from "lucide-react";
import Footer from "../../components/common/layout/Footer";
import Header from "../../components/common/layout/Header";
import TypewriterText from "@/components/common/animations/Typewriter/TypewriterText";

interface ProjectData {
  title: string;
  subtitle: string;
  description: string;
  longDescription: string;
  technologies: string[];
  features: string[];
  challenges: string[];
  solutions: string[];
  stats: { label: string; value: string }[];
  icon: any;
  gradient: string;
}

const projectData: Record<string, ProjectData> = {
  "software-development": {
    title: "Software Development",
    subtitle: "Enterprise-Grade Custom Solutions",
    description: "Revolutionary software development that transforms businesses through cutting-edge technology.",
    longDescription: "We craft bespoke software solutions that push the boundaries of what's possible. Our team of elite developers leverages the latest frameworks, microservices architecture, and cloud-native technologies to build scalable, secure, and performant applications that drive real business value.",
    technologies: ["React", "Node.js", "TypeScript", "Docker", "Kubernetes", "AWS", "PostgreSQL", "Redis", "GraphQL"],
    features: [
      "Microservices Architecture",
      "Real-time Data Processing",
      "Advanced Security Protocols",
      "Scalable Cloud Infrastructure",
      "AI-Powered Analytics",
      "Multi-platform Support"
    ],
    challenges: [
      "Legacy system integration complexity",
      "High-performance requirements",
      "Security compliance standards",
      "Scalability demands"
    ],
    solutions: [
      "Implemented microservices with container orchestration",
      "Optimized database queries and caching strategies",
      "Deployed zero-trust security architecture",
      "Built auto-scaling infrastructure on cloud platforms"
    ],
    stats: [
      { label: "Performance Boost", value: "300%" },
      { label: "Uptime", value: "99.99%" },
      { label: "Response Time", value: "<50ms" },
      { label: "User Satisfaction", value: "98%" }
    ],
    icon: Code,
    gradient: "from-purple-500 via-blue-500 to-cyan-500"
  },
  "app-integration": {
    title: "App Integration",
    subtitle: "Seamless System Connectivity",
    description: "Connect your entire tech ecosystem with intelligent integration solutions.",
    longDescription: "Our integration platform breaks down silos and creates unified workflows across all your applications. Using advanced API gateways, event-driven architectures, and real-time synchronization, we ensure your systems communicate flawlessly.",
    technologies: ["REST APIs", "GraphQL", "WebSockets", "Message Queues", "OAuth 2.0", "Docker", "Kubernetes", "Terraform"],
    features: [
      "Real-time Data Synchronization",
      "API Gateway Management",
      "Event-Driven Architecture",
      "Automated Workflow Orchestration",
      "Error Handling & Retry Logic",
      "Comprehensive Monitoring"
    ],
    challenges: [
      "Multiple system compatibility",
      "Data format inconsistencies",
      "Real-time synchronization needs",
      "Security across integrations"
    ],
    solutions: [
      "Built unified API gateway with rate limiting",
      "Created data transformation pipelines",
      "Implemented event streaming architecture",
      "Deployed OAuth 2.0 and API key management"
    ],
    stats: [
      { label: "Systems Integrated", value: "50+" },
      { label: "API Calls/Day", value: "10M+" },
      { label: "Sync Speed", value: "<100ms" },
      { label: "Uptime", value: "99.95%" }
    ],
    icon: Zap,
    gradient: "from-blue-500 via-purple-500 to-pink-500"
  },
  "social-marketing": {
    title: "Social Marketing",
    subtitle: "AI-Driven Digital Engagement",
    description: "Transform your brand presence with data-driven social media strategies powered by AI.",
    longDescription: "Leverage the power of artificial intelligence and machine learning to create hyper-targeted marketing campaigns. Our platform analyzes millions of data points to optimize content, timing, and audience targeting for maximum engagement and ROI.",
    technologies: ["Python", "TensorFlow", "NLP", "Computer Vision", "React", "Node.js", "MongoDB", "Redis", "AWS"],
    features: [
      "AI Content Generation",
      "Predictive Analytics",
      "Automated A/B Testing",
      "Real-time Performance Tracking",
      "Multi-platform Management",
      "Influencer Analytics"
    ],
    challenges: [
      "Content personalization at scale",
      "Multi-platform management",
      "ROI measurement and attribution",
      "Real-time campaign optimization"
    ],
    solutions: [
      "Deployed GPT-based content generation system",
      "Built unified dashboard for all platforms",
      "Implemented advanced attribution modeling",
      "Created ML-powered optimization engine"
    ],
    stats: [
      { label: "Engagement Rate", value: "+250%" },
      { label: "ROI", value: "400%" },
      { label: "Reach", value: "50M+" },
      { label: "Conversion Rate", value: "+180%" }
    ],
    icon: Rocket,
    gradient: "from-pink-500 via-purple-500 to-indigo-500"
  },
  "smart-vision": {
    title: "Smart Vision",
    subtitle: "Next-Gen AI Computer Vision",
    description: "Revolutionary computer vision solutions powered by deep learning and neural networks.",
    longDescription: "Our Smart Vision platform combines cutting-edge deep learning models with real-time processing capabilities. From object detection to facial recognition, our solutions deliver unprecedented accuracy and speed for industries ranging from healthcare to autonomous vehicles.",
    technologies: ["Python", "PyTorch", "TensorFlow", "OpenCV", "CUDA", "Docker", "Kubernetes", "Edge Computing"],
    features: [
      "Real-time Object Detection",
      "Facial Recognition System",
      "Anomaly Detection",
      "Edge AI Processing",
      "Custom Model Training",
      "Multi-camera Support"
    ],
    challenges: [
      "Real-time processing requirements",
      "Model accuracy optimization",
      "Edge deployment constraints",
      "Large-scale data processing"
    ],
    solutions: [
      "Optimized models with TensorRT",
      "Achieved 99.5% accuracy with custom datasets",
      "Deployed edge inference pipeline",
      "Built distributed training infrastructure"
    ],
    stats: [
      { label: "Accuracy", value: "99.5%" },
      { label: "Processing Speed", value: "60 FPS" },
      { label: "Models Deployed", value: "100+" },
      { label: "Detection Rate", value: "99.8%" }
    ],
    icon: Brain,
    gradient: "from-cyan-500 via-blue-500 to-purple-500"
  },
  "web-design": {
    title: "Web Design",
    subtitle: "Immersive Digital Experiences",
    description: "Create stunning, conversion-optimized websites that captivate and convert visitors.",
    longDescription: "We design and develop websites that don't just look beautiful—they perform. Our team combines cutting-edge design trends with conversion optimization, accessibility, and performance best practices to create digital experiences that drive results.",
    technologies: ["React", "Next.js", "TypeScript", "Tailwind CSS", "Framer Motion", "Three.js", "GSAP", "WebGL"],
    features: [
      "Responsive Design",
      "3D Interactive Elements",
      "Advanced Animations",
      "SEO Optimization",
      "Performance Optimization",
      "Accessibility Compliance"
    ],
    challenges: [
      "Balancing aesthetics with performance",
      "Cross-browser compatibility",
      "Mobile responsiveness",
      "SEO and accessibility requirements"
    ],
    solutions: [
      "Implemented code splitting and lazy loading",
      "Used progressive enhancement strategies",
      "Built mobile-first responsive framework",
      "Applied WCAG 2.1 AA compliance standards"
    ],
    stats: [
      { label: "Page Load Speed", value: "<1s" },
      { label: "Mobile Score", value: "100/100" },
      { label: "Conversion Rate", value: "+220%" },
      { label: "Bounce Rate", value: "-45%" }
    ],
    icon: Globe,
    gradient: "from-indigo-500 via-purple-500 to-pink-500"
  }
};

export default function ProjectDetail() {
  const { slug } = useParams<{ slug: string }>();
  const project = slug ? projectData[slug] : null;

  if (!project) {
    return (
      <div className="min-h-screen text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Project Not Found</h1>
          <Link to="/" className="text-cyan-400 hover:text-cyan-300">Return Home</Link>
        </div>
      </div>
    );
  }

  const IconComponent = project.icon;

  return (
    <div className="min-h-screen text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#000000e0] via-[#1a1a3e] to-[#000000e0] opacity-50" />
        <div className="container mx-auto px-6 relative z-10">
          <Link to="/#projects" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-8 transition-colors">
            <ArrowLeft size={20} />
            <span>Back to Projects</span>
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-r ${project.gradient} mb-6`}>
              <IconComponent size={40} className="text-white" />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold  mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">
              <TypewriterText text={project.title} typingSpeed={0.08} startDelay={0.05} />
            </h1>
            <p className="text-2xl md:text-3xl text-cyan-400 mb-6">{project.subtitle}</p>
            <p className="text-xl text-gray-300 leading-relaxed">{project.longDescription}</p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-y border-white/10">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {project.stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:border-cyan-400/50 transition-all"
              >
                <div className={`text-4xl md:text-5xl font-bold bg-gradient-to-r ${project.gradient} bg-clip-text text-transparent mb-2`}>
                  {stat.value}
                </div>
                <div className="text-gray-300 text-sm md:text-base">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold mb-12 text-center"
          >
            Technology Stack
          </motion.h2>
          <div className="flex flex-wrap gap-4 justify-center">
            {project.technologies.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className={`px-6 py-3 bg-gradient-to-r ${project.gradient} rounded-full text-white font-semibold shadow-lg hover:scale-110 transition-transform`}
              >
                {tech}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white/5">
        <div className="container mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold mb-12 text-center"
          >
            Key Features
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {project.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:border-cyan-400/50 transition-all"
              >
                <Sparkles className="text-cyan-400 mb-3" size={24} />
                <h3 className="text-xl font-semibold mb-2">{feature}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-bold mb-8 flex items-center gap-3">
                <Shield className="text-red-400" size={40} />
                Challenges
              </h2>
              <div className="space-y-4">
                {project.challenges.map((challenge, index) => (
                  <div key={index} className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-gray-300">{challenge}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-bold mb-8 flex items-center gap-3">
                <Rocket className="text-green-400" size={40} />
                Solutions
              </h2>
              <div className="space-y-4">
                {project.solutions.map((solution, index) => (
                  <div key={index} className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-gray-300">{solution}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Project Examples Placeholder */}
      <section className="py-20 bg-white/5">
        <div className="container mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold mb-12 text-center"
          >
            Project Examples
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((item) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: item * 0.1 }}
                className="relative group overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/20"
              >
                <div className="aspect-video bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center">
                  <div className="text-center">
                    <Database size={48} className="text-cyan-400 mx-auto mb-4" />
                    <p className="text-gray-300 text-sm">Project Example {item}</p>
                    <p className="text-xs text-gray-500 mt-2">Placeholder for project showcase</p>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Example Project {item}</h3>
                  <p className="text-gray-300 text-sm">
                    Detailed case study and project examples will be displayed here. This section showcases real-world implementations and success stories.
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's discuss how we can bring your vision to life with cutting-edge technology.
            </p>
            <Link
              to="/#contact"
              className={`inline-block px-8 py-4 bg-gradient-to-r ${project.gradient} rounded-full text-white font-semibold text-lg hover:scale-105 transition-transform shadow-lg hover:shadow-xl`}
            >
              Get Started
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

